import { ReplayTimeline } from '@/feature/replay'

import { ModalBase } from '@/lib/core/components/Modal'

import { useBodyScrollLock } from '@/lib/core/hooks/useBodyScrollLock'

import { TaskProgressPanel } from '../TaskProgressPanel'
import { TaskFocusMap } from './TaskFocusMap'
import styles from './TaskFocusModal.module.css'
import { TaskFocusModalProps } from './TaskFocusModal.types'
import { useTaskReplay } from './useTaskReplay'

/**
 * Task focus mode, in place on the Execution Board: the task's route on a map beside its detail
 * panel. Replaces navigating away to the Observatory, and works for completed tasks too, which the
 * Observatory's live map layer never renders (BRI-1477).
 *
 * Mount this conditionally — ModalBase renders its children whether the dialog is open or not, and
 * the map must not be built while the dialog is closed. See TaskFocusMap.
 */
export const TaskFocusModal = ({ task, tripProgress, onClose }: TaskFocusModalProps) => {
  // showModal() makes the board behind the dialog inert but leaves it scrollable, so lock it.
  // Mounted only while open, so the lock's lifetime is exactly the modal's.
  useBodyScrollLock(true)

  const replay = useTaskReplay(task)

  return (
    <ModalBase
      isActive
      className={styles.modal}
      onEscape={onClose}
      dataTestId={`task-focus-modal-${task.id}`}
    >
      <div className={styles.container}>
        <div className={styles.mapColumn}>
          <TaskFocusMap task={task} replay={replay} />
          {replay.replayAsset ? (
            <ReplayTimeline
              asset={replay.replayAsset}
              rangeStartTime={replay.rangeStartTime}
              rangeEndTime={replay.rangeEndTime}
              selectionStartTime={replay.selectionStartTime}
              selectionEndTime={replay.selectionEndTime}
              setSelectionStartTime={replay.setSelectionStartTime}
              setSelectionEndTime={replay.setSelectionEndTime}
              stepSize={replay.stepSize}
              setStepSize={replay.setStepSize}
              history={replay.replayHistory}
            />
          ) : null}
        </div>
        <div className={styles.detailPane} data-testid="task-focus-detail-pane">
          <TaskProgressPanel
            task={task}
            showProgress
            tripProgress={tripProgress}
            onClose={onClose}
            testId={`task-focus-panel-${task.taskDefinition.id}`}
          />
        </div>
      </div>
    </ModalBase>
  )
}
